import React, { useState } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WarningIcon from "@material-ui/icons/Warning";
import { getLang, getApiLang } from "app/feature/constants";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: "#fff3e0",
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerIcon: {
    color: theme.palette.warning.main,
    fontSize: 24,
  },
  headerText: {
    fontWeight: 600,
    color: theme.palette.warning.dark,
  },
  accordion: {
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    "&:before": {
      display: "none",
    },
  },
  accordionSummary: {
    backgroundColor: "#f5f5f5",
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
    padding: theme.spacing(0, 2),
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  accordionDetails: {
    padding: 0,
    display: 'block',
    width: '100%',
  },
  sheetHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
  sheetName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  prizeName: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  tableContainer: {
    maxHeight: 300,
    width: '100%',
  },
  table: {
    width: '100%',
  },
  serialNumberCell: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    padding: theme.spacing(1, 2),
  },
  indexCell: {
    width: 80,
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  validChip: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
    fontSize: "0.75rem",
    height: 24,
  },
  invalidChip: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontSize: "0.75rem",
    height: 24,
  },
  summary: {
    padding: theme.spacing(1, 2),
    backgroundColor: "#fafafa",
    borderTop: `1px solid ${theme.palette.divider}`,
    width: '100%',
  },
  summaryText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  countBadges: {
    display: "flex",
    gap: theme.spacing(1),
  },
  validBadge: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  invalidBadge: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  warningBadge: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  overallSummary: {
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

export default function BatchInvalidSerialsTableComponent({
  validationResult = {},
  lang,
}) {
  const classes = useStyles();
  const [expandedPanels, setExpandedPanels] = useState(new Set());

  
  // Helper function to process error messages
  const processErrorMessage = (errorMsg) => {
    if (!errorMsg) return "";
    
    if (typeof errorMsg === 'string') {
      let apiKey = errorMsg;
      
      if (errorMsg.startsWith('error.')) {
        apiKey = errorMsg.substring(6);
      }
      
      const localizedMsg = getApiLang(lang, apiKey);
      if (localizedMsg && localizedMsg !== apiKey) {
        return localizedMsg;
      }
      
      const fallbackMsg = getLang(lang, apiKey);
      if (fallbackMsg && fallbackMsg !== apiKey) {
        return fallbackMsg;
      }
      
      if (lang?.fallbackApiData?.error && lang.fallbackApiData.error[apiKey]) {
        const manualFallback = lang.fallbackApiData.error[apiKey];
        return manualFallback;
      }
    }
    
    return errorMsg;
  };

  const processErrorMessageShort = (errorMsg) => {
    if (!errorMsg) return "";
    
    if (typeof errorMsg === 'string') {
      let apiKey = errorMsg;
      
      if (errorMsg.startsWith('error.')) {
        apiKey = errorMsg.substring(6);
      }
      
      if (apiKey === 'ERROR_LUCKY_DRAW_INSUFFICIENT_PRIZE_SPOTS') {
        apiKey = 'ERROR_LUCKY_DRAW_INSUFFICIENT_PRIZE_SPOTS_SHORT';
      }
      
      const localizedMsg = getApiLang(lang, apiKey);
      if (localizedMsg && localizedMsg !== apiKey) {
        return localizedMsg;
      }
      
      const fallbackMsg = getLang(lang, apiKey);
      if (fallbackMsg && fallbackMsg !== apiKey) {
        return fallbackMsg;
      }
      
      if (lang?.fallbackApiData?.error && lang.fallbackApiData.error[apiKey]) {
        const manualFallback = lang.fallbackApiData.error[apiKey];
        return manualFallback;
      }
    }
    
    return errorMsg;
  };

  // Early return if no validation result
  if (!validationResult || !validationResult.sheets) {
    return null;
  }

  // Helper function to ensure array format
  const ensureArray = (data) => {
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === 'object') {
      return Object.values(data);
    }
    return [];
  };

  // Process validation results to extract sheets with errors
  const sheetsWithIssues = Object.entries(validationResult.sheets || {}).filter(([sheetName, result]) => {
    if (!result.success) return true;
    
    // Check if there are invalid serial numbers in the data
    if (result.data && result.data.all_serials_status) {
      const invalidSerials = ensureArray(result.data.all_serials_status.invalid);
      return invalidSerials.length > 0;
    }
    
    return false;
  });

  if (sheetsWithIssues.length === 0) {
    return null;
  }

  const handleAccordionChange = (sheetName) => (event, isExpanded) => {
    const newExpanded = new Set(expandedPanels);
    if (isExpanded) {
      newExpanded.add(sheetName);
    } else {
      newExpanded.delete(sheetName);
    }
    setExpandedPanels(newExpanded);
  };

  // Calculate overall statistics
  let totalValid = 0;
  let totalInvalid = 0;
  let sheetsWithErrors = 0;

  sheetsWithIssues.forEach(([sheetName, result]) => {
    if (result.data && result.data.all_serials_status) {
      const validSerials = ensureArray(result.data.all_serials_status.valid);
      const invalidSerials = ensureArray(result.data.all_serials_status.invalid);
      totalValid += validSerials.length;
      totalInvalid += invalidSerials.length;
      if (invalidSerials.length > 0) sheetsWithErrors++;
    } else if (!result.success) {
      sheetsWithErrors++;
    }
  });

  // Helper function to get pluralized sheet label
  const getSheetLabel = (count) => {
    return count === 1 
      ? getLang(lang, "label.SHEET_WITH_ISSUES") || "Sheet with Issues"
      : getLang(lang, "label.SHEETS_WITH_ISSUES") || "Sheets with Issues";
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <Box className={classes.header}>
        <WarningIcon className={classes.headerIcon} />
        <Typography variant="subtitle2" className={classes.headerText}>
          {getLang(lang, "label.BATCH_VALIDATION_ISSUES") || "Batch Validation Issues"}
        </Typography>
        <div className={classes.countBadges}>
          <span className={classes.warningBadge}>
            {sheetsWithErrors} {getSheetLabel(sheetsWithErrors)}
          </span>
          {totalValid > 0 && (
            <span className={classes.validBadge}>
              {totalValid} {getLang(lang, "label.VALID") || "Valid"}
            </span>
          )}
          {totalInvalid > 0 && (
            <span className={classes.invalidBadge}>
              {totalInvalid} {getLang(lang, "label.INVALID") || "Invalid"}
            </span>
          )}
        </div>
      </Box>

      {sheetsWithIssues.map(([sheetName, result]) => {
        const hasValidationData = result.data && result.data.all_serials_status;
        
        let validSerials = [];
        let invalidSerials = [];
        let serialNumbersWithStatus = [];
        
        if (hasValidationData) {
          validSerials = ensureArray(result.data.all_serials_status.valid);
          invalidSerials = ensureArray(result.data.all_serials_status.invalid);
          
          validSerials.forEach(sn => {
            if (sn && typeof sn === 'string' && sn.trim() !== '') {
              serialNumbersWithStatus.push({ serialNumber: sn, status: 'valid' });
            }
          });
          
          invalidSerials.forEach(sn => {
            if (sn && typeof sn === 'string' && sn.trim() !== '') {
              serialNumbersWithStatus.push({ serialNumber: sn, status: 'invalid' });
            }
          });
          
          // Sort to show invalid ones first
          serialNumbersWithStatus.sort((a, b) => {
            if (a.status === 'invalid' && b.status === 'valid') return -1;
            if (a.status === 'valid' && b.status === 'invalid') return 1;
            return a.serialNumber.localeCompare(b.serialNumber);
          });
        }

        const validCount = validSerials.length;
        const invalidCount = invalidSerials.length;
        const isExpanded = expandedPanels.has(sheetName);

        return (
          <Accordion 
            key={sheetName}
            className={classes.accordion}
            expanded={isExpanded}
            onChange={handleAccordionChange(sheetName)}
          >
            <AccordionSummary 
              className={classes.accordionSummary}
              expandIcon={<ExpandMoreIcon />}
            >
              <Box className={classes.sheetHeader}>
                <Box>
                  <Typography variant="subtitle2" className={classes.sheetName}>
                    Sheet: {sheetName}
                  </Typography>
                  {result.prize_name && (
                    <Typography variant="caption" className={classes.prizeName}>
                      Prize: {result.prize_name}
                    </Typography>
                  )}
                </Box>
                <Box style={{ marginLeft: 'auto' }}>
                  <div className={classes.countBadges}>
                    {!result.success && !hasValidationData && (
                      <span className={classes.invalidBadge}>
                        {result.error === 'SHEET_NOT_MATCHING_PRIZE' 
                          ? (getLang(lang, "label.SHEET_NOT_MATCHING_PRIZE") || "Sheet Not Matching Prize")
                          : result.error === 'NO_DATA'
                          ? (getLang(lang, "label.NO_DATA") || "No Data")
                          : processErrorMessageShort(result.error) || (getLang(lang, "label.ERROR") || "Error")
                        }
                      </span>
                    )}
                    {hasValidationData && validCount > 0 && (
                      <span className={classes.validBadge}>
                        {validCount} {getLang(lang, "label.VALID") || "Valid"}
                      </span>
                    )}
                    {hasValidationData && invalidCount > 0 && (
                      <span className={classes.invalidBadge}>
                        {invalidCount} {getLang(lang, "label.INVALID") || "Invalid"}
                      </span>
                    )}
                  </div>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails className={classes.accordionDetails}>
              {!result.success && !hasValidationData ? (
                <Box p={2}>
                  <Typography variant="body2" color="error">
                    {result.error === 'SHEET_NOT_MATCHING_PRIZE' 
                      ? (getLang(lang, "paragraph.SHEET_NOT_MATCHING_PRIZE_ERROR") || 
                         `Sheet name "${sheetName}" does not match any prize name. Please ensure sheet names exactly match your prize names.`).replace('{sheetName}', sheetName)
                      : result.error === 'NO_DATA'
                      ? (getLang(lang, "paragraph.NO_DATA_ERROR_MESSAGE") || 
                         `Sheet "${sheetName}" contains no data rows.`).replace('{sheetName}', sheetName)
                      : processErrorMessage(result.message || result.error) || (getLang(lang, "paragraph.GENERIC_SHEET_ERROR") || 'An error occurred while processing this sheet.')
                    }
                  </Typography>
                </Box>
              ) : serialNumbersWithStatus.length > 0 ? (
                <>
                  <TableContainer className={classes.tableContainer}>
                    <Table className={classes.table} size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.indexCell}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                              #
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                              {getLang(lang, "label.SERIAL_NUMBER") || "Serial Number"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                              {getLang(lang, "label.STATUS") || "Status"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serialNumbersWithStatus.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell className={classes.indexCell}>
                              {index + 1}
                            </TableCell>
                            <TableCell className={classes.serialNumberCell}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item.status === 'invalid' ? (
                                  <WarningIcon style={{ color: '#ff9800', fontSize: 16 }} />
                                ) : (
                                  <CheckCircleIcon style={{ color: '#4caf50', fontSize: 16 }} />
                                )}
                                {item.serialNumber}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {item.status === 'valid' ? (
                                <Chip
                                  icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
                                  label={getLang(lang, "label.VALID") || "Valid"}
                                  size="small"
                                  className={classes.validChip}
                                />
                              ) : (
                                <Chip
                                  icon={<ErrorIcon style={{ fontSize: 16 }} />}
                                  label={getLang(lang, "label.NOT_FOUND") || "Not Found"}
                                  size="small"
                                  className={classes.invalidChip}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box className={classes.summary}>
                    <Typography variant="body2" className={classes.summaryText}>
                      {invalidCount > 0 
                        ? (getLang(lang, "paragraph.INVALID_SERIALS_HELP_TEXT") || 
                           "Please verify these serial numbers exist or not in the SmartKood and correct them in your Excel file.")
                        : (getLang(lang, "paragraph.ALL_SERIALS_VALID") || 
                           "All serial numbers are valid for this sheet.")
                      }
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box p={2}>
                  <Typography variant="body2" color="textSecondary">
                    {getLang(lang, "label.NO_DATA_AVAILABLE_FOR_SHEET") || 
                     "No serial number data available for this sheet."}
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Box className={classes.overallSummary}>
        <Typography variant="body2" className={classes.summaryText}>
          {totalInvalid > 0 
            ? (getLang(lang, "paragraph.BATCH_VALIDATION_HAS_ERRORS") || 
               `Please correct the invalid serial numbers in {count} sheet{plural} before proceeding with the import.`)
               .replace('{count}', sheetsWithErrors)
               .replace('{plural}', sheetsWithErrors !== 1 ? 's' : '')
            : (getLang(lang, "paragraph.BATCH_VALIDATION_COMPLETE_SUCCESS") || 
               "All sheets have been validated successfully and are ready for import.")
          }
        </Typography>
      </Box>
    </Paper>
  );
} 